/**
 * TypeSelect — location-scoped Type drop-down used by each editable row.
 *
 * Mirrors PlanSelect: the open menu shows each option's full label (grouped
 * NEW / USED) but the collapsed trigger shows only the bare code (e.g. "CV")
 * to save grid width — behavior a native <select> can't provide. Shared
 * open/close + fixed-positioning plumbing lives in `useAnchoredMenu`.
 *
 * Driven by `useNutSheetTypes(locationId)`; the parent gets back
 * `(code, category)` so callers can mirror the category onto the entry record.
 */

import React from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useNutSheetTypes } from '../../hooks/useNutSheetTypes';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import { NutSheetCategory } from '../../types/nutSheet';

interface TypeSelectProps {
  locationId: number;
  value: string;
  onChange: (code: string, category: NutSheetCategory) => void;
  disabled?: boolean;
}

const groupHeader =
  'px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400';

export const TypeSelect: React.FC<TypeSelectProps> = ({
  locationId,
  value,
  onChange,
  disabled,
}) => {
  const { newTypes, usedTypes, loading } = useNutSheetTypes(locationId);
  const { open, setOpen, pos, btnRef, menuRef } = useAnchoredMenu();

  const select = (code: string, category: NutSheetCategory) => {
    onChange(code, category);
    setOpen(false);
    // Return focus to the trigger (native-<select> behavior). The click focused
    // the menu's option button, and unmounting a focused node fires no blur —
    // without this, the row's blur-commit never runs and the selection is
    // silently never saved.
    btnRef.current?.focus();
  };

  // Full label for the current code (NEW wins if a code somehow appears in both)
  // — surfaced as the trigger's hover tooltip since the trigger shows only code.
  const selectedLabel =
    newTypes.find((t) => t.code === value)?.label ??
    usedTypes.find((t) => t.code === value)?.label ??
    '';

  const noTypes = !loading && newTypes.length === 0 && usedTypes.length === 0;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        title={selectedLabel || 'Select type'}
        className="flex items-center justify-between w-full rounded border border-gray-500 bg-white px-1 py-0.5 text-xs disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
      >
        <span className={value ? 'font-medium' : 'text-gray-400'}>{value || '—'}</span>
        <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width, maxHeight: pos.maxHeight }}
          className="z-50 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-xs shadow-lg"
        >
          <button
            type="button"
            onClick={() => select('', 'NEW')}
            className="block w-full px-3 py-1 text-left text-gray-400 hover:bg-gray-100"
          >
            — (clear)
          </button>
          {loading && <div className="px-3 py-1 italic text-gray-400">Loading…</div>}
          {noTypes && <div className="px-3 py-1 italic text-gray-400">No types configured</div>}
          {newTypes.length > 0 && (
            <>
              <div className={groupHeader}>New</div>
              {newTypes.map((t) => (
                <button
                  key={`N-${t.code}`}
                  type="button"
                  onClick={() => select(t.code, 'NEW')}
                  className={`block w-full px-3 py-1 text-left hover:bg-gray-100 ${
                    value === t.code ? 'bg-blue-50 font-medium' : ''
                  }`}
                >
                  {t.label} <span className="text-gray-400">({t.code})</span>
                </button>
              ))}
            </>
          )}
          {usedTypes.length > 0 && (
            <>
              <div className={groupHeader}>Used</div>
              {usedTypes.map((t) => (
                <button
                  key={`U-${t.code}`}
                  type="button"
                  onClick={() => select(t.code, 'USED')}
                  className={`block w-full px-3 py-1 text-left hover:bg-gray-100 ${
                    value === t.code ? 'bg-blue-50 font-medium' : ''
                  }`}
                >
                  {t.label} <span className="text-gray-400">({t.code})</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
};
